import { CalendarIcon, ChevronRightIcon, SaveIcon, UserIcon } from "lucide-react";
import { ConfigChangelogEntry } from "@shared/otherTypes";
import { useMemo, useState } from "react";
import { dateToLocaleDateString, dateToLocaleTimeString, isDateToday, tsToLocaleDateTimeString } from "@/lib/dateTime";
import TxAnchor from "@/components/TxAnchor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "wouter";


type PageHeaderChangelogProps = {
    changelogData?: ConfigChangelogEntry[];
}
export function PageHeaderChangelog({ changelogData }: PageHeaderChangelogProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const mostRecent = useMemo(() => {
        if (!changelogData?.length) return null;
        const last = changelogData[changelogData.length - 1];
        const lastDate = new Date(last.ts);
        const timeStr = dateToLocaleTimeString(lastDate, '2-digit', '2-digit');
        const dateStr = dateToLocaleDateString(lastDate, 'long');
        const titleTimeIndicator = isDateToday(lastDate) ? timeStr : dateStr;
        return {
            author: last.author,
            dateTime: titleTimeIndicator,
        }
    }, [changelogData]);

    const reversedChangelog = useMemo(() => {
        if (!changelogData) return null;
        return [...changelogData].reverse();
    }, [changelogData]);

    const handleOpenChangelog = () => {
        setIsModalOpen(true);
    }

    const placeholder = Array.isArray(changelogData) ? 'Keine Änderungen' : 'lädt …';

    return (<>
        <button
            type="button"
            className="group flex items-center gap-3 rounded-lg border border-fc-stroke bg-fc-glass px-3 py-2 text-left transition-colors hover:border-fc-accent/35 hover:bg-fc-accent/[0.08] disabled:cursor-default disabled:hover:border-fc-stroke disabled:hover:bg-fc-glass"
            onClick={handleOpenChangelog}
            disabled={!reversedChangelog?.length}
        >
            <SaveIcon className="size-4 text-fc-accent-bright" />
            <span className="flex flex-col gap-0.5">
                <span className="fc-kicker">Zuletzt geändert</span>
                <span className="flex items-center gap-1.5 text-[12.5px] text-white">
                    <CalendarIcon className="size-3.5 text-fc-txt3" /> {mostRecent?.dateTime ?? placeholder}
                </span>
            </span>
        </button>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className='max-w-xl'>
                <DialogHeader>
                    <DialogTitle>Letzte Änderungen</DialogTitle>
                </DialogHeader>
                <div className="max-h-[70vh] space-y-2 overflow-auto pr-2">
                    {reversedChangelog?.map((entry, i) => <ChangelogEntry key={i} entry={entry} />)}
                </div>
            </DialogContent>
        </Dialog>
    </>)
}


function ChangelogEntry({ entry }: { entry: ConfigChangelogEntry }) {
    return (
        <div className='flex flex-col gap-2 rounded-row border border-fc-stroke bg-fc-glass px-3 py-2.5'>
            <div className="flex items-center justify-between gap-3">
                <div className='flex items-center gap-2 text-[13px] font-semibold text-fc-accent-bright'>
                    <UserIcon className='size-4 opacity-70' />
                    {entry.author}
                </div>
                <div className='text-[11.5px] text-fc-txt3'>
                    {tsToLocaleDateTimeString(entry.ts, 'short', 'short')}
                </div>
            </div>
            <div className='flex flex-wrap gap-1 text-[12px]'>
                {entry.keys.length ? entry.keys.map((cfg, index) => (
                    <span key={cfg}>
                        <span className='inline rounded-md bg-fc-well px-1.5 py-0.5 font-mono tracking-wide text-fc-txt2'>{cfg}</span>
                        {index < entry.keys.length - 1 && ','}
                    </span>
                )) : (
                    <div className='italic text-fc-txt3'>Keine Änderungen</div>
                )}
            </div>
        </div>
    )
}


type PageHeaderLinksProps = {
    topLabel: string;
    topLink: string;
    bottomLabel: string;
    bottomLink: string;
}
export function PageHeaderLinks(props: PageHeaderLinksProps) {
    return (
        <div className='flex flex-col gap-0.5 text-[12.5px]'>
            <TxAnchor href={props.topLink}>{props.topLabel}</TxAnchor>
            <TxAnchor href={props.bottomLink}>{props.bottomLabel}</TxAnchor>
        </div>
    )
}


type PageHeaderProps = {
    title: string;
    icon: React.ReactNode;
    parentName?: string;
    parentLink?: string;
    children?: React.ReactNode;
}
export function PageHeader(props: PageHeaderProps) {
    return (
        <header className='fc-content-head'>
            <div className='fc-content-head-title'>
                <span className='fc-content-head-icon'>{props.icon}</span>
                <div className='fc-content-head-copy'>
                    {props.parentName && props.parentLink ? (
                        <div className='fc-content-head-crumb'>
                            <Link href={props.parentLink}>{props.parentName}</Link>
                            <ChevronRightIcon />
                        </div>
                    ) : (
                        <div className='fc-content-head-crumb'><span>FC Admin</span></div>
                    )}
                    <h1>{props.title}</h1>
                </div>
            </div>
            {props.children ? <div className='fc-content-head-actions'>{props.children}</div> : null}
        </header>
    )
}
